/**
 * KAN-746: отображение заказа: номер, статус бейджем, суммы, даты.
 * Общие для списка заказов, карточки заказа и блока заказов клиента.
 */
import type { BadgeProps } from '@/components/ui/badge';
import type { OrderDetail, OrderStatus, OrderTab } from '@/types/order';

/** Сумма из API (число, строка или null) в число; не число: 0. */
export function toAmount(value: number | string | null | undefined): number {
  if (value === undefined || value === null || value === '') return 0;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : 0;
}

/** "Заказ #12"; без номера: "Заказ". */
export function orderTitle(shortId: number | string | null | undefined): string {
  return shortId !== null && shortId !== undefined && shortId !== '' ? `Заказ #${shortId}` : 'Заказ';
}

/** "#12"; без номера: "-". */
export function orderNumber(shortId: number | string | null | undefined): string {
  return shortId !== null && shortId !== undefined && shortId !== '' ? `#${shortId}` : '-';
}

interface StatusView {
  label: string;
  variant: NonNullable<BadgeProps['variant']>;
}

export const ORDER_STATUS_VIEW: Record<OrderStatus, StatusView> = {
  DRAFT: { label: 'Корзина', variant: 'secondary' },
  PENDING_REVIEW: { label: 'Оформлен', variant: 'pending' },
  APPROVED: { label: 'Подтверждён', variant: 'completed' },
  CANCELLED: { label: 'Отменён', variant: 'failed' },
  REJECTED: { label: 'Отклонён', variant: 'failed' },
};

export function orderStatusView(status: OrderStatus): StatusView {
  return ORDER_STATUS_VIEW[status] ?? { label: status, variant: 'secondary' };
}

export const ORDER_TAB_LABELS: Record<OrderTab, string> = {
  carts: 'Корзины',
  placed: 'Оформленные',
  approved: 'Подтверждённые',
  cancelled: 'Отменённые',
};

/** "09.10.2026, 14:05"; без даты: "-". */
export function formatOrderDate(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function nullableAmount(value: number | string | null | undefined): number | null {
  return value === null || value === undefined || value === '' ? null : toAmount(value);
}

/** KAN-755: скидка больше нуля или null (строки скидки нет). */
export function discountAmount(value: number | string | null | undefined): number | null {
  const amount = toAmount(value);
  return amount > 0 ? amount : null;
}

/**
 * KAN-759: суммы заказа для OrderTotals (доставки и наценки нет: строки нет).
 * KAN-755: скидка (null, если её нет) и промокод.
 */
export function orderTotalsValues(
  order: Pick<OrderDetail, 'subtotal' | 'delivery' | 'markup' | 'total' | 'discount' | 'promo_code'>,
): {
  subtotal: number;
  delivery: number | null;
  markup: number | null;
  total: number;
  discount: number | null;
  promoCode: string | null;
} {
  return {
    subtotal: toAmount(order.subtotal),
    delivery: nullableAmount(order.delivery),
    markup: nullableAmount(order.markup),
    total: toAmount(order.total),
    discount: discountAmount(order.discount),
    promoCode: order.promo_code || null,
  };
}
